/**
 * Shared Email Preview Dialog Component
 * Used by both webinar-mailings and plx-mailings
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

export interface PreviewClient {
  code: string;
  name: string;
}

export interface EmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  previewHtml: string;
  subject?: string;
  clients: PreviewClient[];
  selectedClient: string;
  onClientChange: (clientCode: string) => void;
  loading?: boolean;
  title?: string;
  /** Optional info box content (e.g., for dynamic date explanation) */
  infoBox?: React.ReactNode;
}

const EmailPreviewDialog: React.FC<EmailPreviewDialogProps> = ({
  open,
  onClose,
  previewHtml,
  subject,
  clients,
  selectedClient,
  onClientChange,
  loading = false,
  title = 'E-Mail Vorschau',
  infoBox,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <VisibilityIcon color="primary" />
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Vorschau mit ersetzten Platzhaltern. So sieht die E-Mail für Empfänger aus.
        </Typography>

        {/* Optional Info Box */}
        {infoBox}

        {/* Client Selector */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Client-Vorschau</InputLabel>
            <Select
              value={selectedClient}
              onChange={(e) => onClientChange(e.target.value)}
              label="Client-Vorschau"
            >
              {clients.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading && <CircularProgress size={20} />}
        </Box>

        {/* Subject Preview */}
        {subject && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Betreff:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {subject}
            </Typography>
          </Box>
        )}

        {/* HTML Preview in iframe */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#e0e0e0',
            p: 2,
            borderRadius: 1,
            minHeight: 520,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              width: '640px',
              height: '500px',
              overflow: 'hidden',
              border: '1px solid #ccc',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <iframe
              srcDoc={previewHtml}
              title="E-Mail Vorschau"
              style={{
                width: '640px',
                height: '500px',
                border: 'none',
                backgroundColor: '#fff',
              }}
              sandbox="allow-same-origin"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewDialog;
